import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import Bande from '../components/Bande';
import FilmHero from '../components/FilmHero';
import NoteHero from '../components/NoteHero';
import Reveal from '../components/Reveal';
import TitreSection from '../components/TitreSection';
import BandeauReassurance from '../components/BandeauReassurance';
import CarteMachine from '../components/CarteMachine';
import CarteAccessoire from '../components/CarteAccessoire';
import AppelAction from '../components/AppelAction';
import Chiffres from '../components/Chiffres';
import Avis from '../components/Avis';
import Faq from '../components/Faq';
import SectionContact from '../components/SectionContact';
import { ENTREPRISE, MACHINES, ACCESSOIRES, SECTEURS, ETAPES, COMMUNES_LISTE, FAQ } from '../data/site';

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': ENTREPRISE.site + '/#entreprise',
  name: ENTREPRISE.nom,
  description:
    "Location d'engins de chantier avec ou sans chauffeur à Alfortville (94) : mini-pelles Volvo et Sunward, chariot télescopique JCB, chariot élévateur, nacelle élévatrice et compresseur. Livraison sur chantier en Île-de-France.",
  url: ENTREPRISE.site,
  telephone: '+33601244724',
  email: ENTREPRISE.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: ENTREPRISE.adresse,
    postalCode: ENTREPRISE.codePostal,
    addressLocality: ENTREPRISE.ville,
    addressRegion: 'Île-de-France',
    addressCountry: 'FR',
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '17:00',
    },
  ],
  areaServed: COMMUNES_LISTE.map((c) => ({ '@type': 'City', name: c })),
  makesOffer: MACHINES.map((m) => ({
    '@type': 'Offer',
    itemOffered: { '@type': 'Product', name: m.nom, description: m.type },
  })),
};

const jsonLdFaq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ.filter((f) => f.accueil).map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.r },
  })),
};

export default function Accueil() {
  return (
    <>
      <Seo
        titre="Location d'engins de chantier sans chauffeur | Alfortville 94"
        description="Mini-pelles, chariots élévateurs, nacelle et compresseur en location, avec ou sans chauffeur, à Alfortville (94). Livraison sur votre chantier en Île-de-France sous 24 à 48 h."
        jsonLd={[jsonLd, jsonLdFaq]}
      />

      {/* ---------- HERO ---------- */}
      <section className="relative bg-nuit text-white overflow-hidden min-h-[600px] lg:min-h-[650px]">
        <FilmHero />

        <div className="conteneur relative pt-14 pb-16 lg:pt-16 lg:pb-20">
          <div className="max-w-[560px]">
            <div className="relative z-10">
              <h1 className="font-display uppercase text-[38px] sm:text-[52px] lg:text-[48px] xl:text-[58px] leading-[0.98]">
                Vos machines,
                <br />
                livrées <span className="mot-degrade">prêtes</span>
                <br />
                à travailler
              </h1>
              <p className="mt-5 text-[17px] sm:text-[18.5px] text-white/75 max-w-xl leading-relaxed">
                Mini-pelles, chariots élévateurs, nacelle et compresseur en location, avec ou
                sans chauffeur, pour les professionnels du bâtiment, des travaux publics et de l'industrie.
                Au départ d'Alfortville, révisés, assurés, sur votre chantier partout en
                Île-de-France sous 24 à 48 h.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="btn-jaune whitespace-nowrap">Demander mon devis</Link>
                <a href={ENTREPRISE.telephoneLien} className="btn-contour whitespace-nowrap">
                  Appeler le {ENTREPRISE.telephone}
                </a>
              </div>

              <NoteHero />

              <dl className="mt-8 pt-6 border-t border-white/15 grid grid-cols-2 gap-x-6 gap-y-5 max-w-[520px]">
                {[
                  ['24 à 48 h', 'sur votre chantier'],
                  ['Assurées', 'révisées, prêtes'],
                  ['Devis rapide', 'réponse le jour même'],
                  ['Journée', 'semaine ou mois'],
                ].map(([t, s]) => (
                  <div key={t}>
                    <dt className="font-display text-[17px] text-jaune uppercase leading-tight">{t}</dt>
                    <dd className="font-cond uppercase tracking-[0.08em] text-[13px] text-white/60 mt-1">{s}</dd>
                  </div>
                ))}
              </dl>
            </div>

          </div>
        </div>
        <Bande hauteur={8} />
      </section>

      <BandeauReassurance />

      {/* ---------- POURQUOI NOUS ---------- */}
      <section className="bg-white py-16 sm:py-20">
        <div className="conteneur">
          <TitreSection
            avant="Trois raisons très "
            mot="concrètes"
          />
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {[
              {
                t: 'On vous dit ce qui passe chez vous',
                p: "Un accès de 1,60 m, un terrain en pente, un sous-sol : donnez-nous la contrainte, on vous répond avec la machine qui convient. Pas un catalogue, une réponse.",
              },
              {
                t: "Vous ne sortez pas votre camion",
                p: "On livre et on reprend la machine sur votre chantier. Vous n'immobilisez ni votre porte-engins, ni votre chauffeur, et vous ne vous occupez pas des restrictions de circulation.",
              },
              {
                t: 'Une équipe, pas un standard',
                p: "Neuf personnes qui font ce métier depuis des années. Vous appelez, vous avez quelqu'un qui connaît les machines. En cas de souci pendant la location, on intervient dans la journée.",
              },
            ].map((b, i) => (
              <Reveal key={b.t} delai={i * 0.08}>
                <div className="h-full carte p-7 border-t-4 border-t-jaune">
                  <h3 className="font-cond uppercase font-bold tracking-[0.02em] text-[20px] text-nuit leading-tight">
                    {b.t}
                  </h3>
                  <p className="mt-3 text-nuit/75">{b.p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- LE PARC ---------- */}
      <section className="bg-trame py-16 sm:py-20" id="parc">
        <div className="conteneur">
          <TitreSection
            avant="Les machines "
            mot="disponibles"
            apres=" à la location"
          />
          <p className="mt-4 max-w-2xl text-nuit/75">
            Notre parc compte 18 machines, du terrassement au travail en hauteur. Voici les
            principales, avec leurs vraies caractéristiques : vous vérifiez avant d'appeler si
            la largeur passe, si la profondeur de fouille suffit et quel CACES il vous faut.
          </p>

          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {MACHINES.map((m) => (
              <Reveal key={m.slug}>
                <CarteMachine machine={m} />
              </Reveal>
            ))}
          </div>

          <div className="mt-12">
            <h3 className="font-cond uppercase font-bold tracking-[0.06em] text-[19px] text-nuit">
              Les accessoires qui vont avec
            </h3>
            <div className="mt-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {ACCESSOIRES.map((a, i) => (
                <Reveal key={a.nom} delai={i * 0.06}>
                  <CarteAccessoire accessoire={a} hauteur={180} />
                </Reveal>
              ))}
            </div>
          </div>

            <div className="mt-8 carte p-6 border-l-4 border-l-bleu max-w-3xl">
              <p className="text-nuit/85">
                <strong className="font-semibold">Le parc ne s'arrête pas là.</strong> Nous
                disposons de 18 machines et d'un jeu complet d'accessoires. Si vous cherchez
                un tonnage ou un équipement qui ne figure pas sur cette page, appelez-nous :
                il y a de fortes chances que nous l'ayons.
              </p>
            </div>
        </div>
      </section>

      <AppelAction
        titre="Une machine en tête, ou aucune idée ?"
        texte="Les deux nous vont. Décrivez le chantier, on vous dit ce qui passe et on vous envoie le tarif en quelques heures."
      />

      {/* ---------- POUR QUI ---------- */}
      <section className="bg-white py-16 sm:py-20">
        <div className="conteneur">
          <TitreSection avant="Nos machines partent sur vos " mot="chantiers" />
          <div className="mt-10 grid gap-x-8 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
            {SECTEURS.map((s, i) => (
              <Reveal key={s.titre} delai={i * 0.05}>
                <div className="border-l-4 border-bleu pl-5">
                  <h3 className="font-cond uppercase font-bold tracking-[0.03em] text-[19px] text-nuit">{s.titre}</h3>
                  <p className="mt-2 text-nuit/75 text-[16px]">{s.texte}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 carte p-6 border-l-4 border-l-jaune max-w-3xl">
            <p className="text-nuit/85">
              <strong className="font-semibold">Nous travaillons avec les professionnels.</strong>{' '}
              Bâtiment, travaux publics, industrie et logistique. C'est un choix assumé : nos
              machines partent sur des chantiers encadrés, avec des conducteurs formés ou avec
              l'un de nos chauffeurs.
            </p>
          </div>
        </div>
      </section>

      <AppelAction variante="sombre" titre="Votre secteur figure dans la liste ?" texte="Alors on a probablement déjà travaillé sur un chantier comme le vôtre. Parlons-en." />

      {/* ---------- COMMENT CA MARCHE ---------- */}
      <section className="bg-white py-16 sm:py-20">
        <div className="conteneur">
          <TitreSection avant="Quatre étapes, " mot="pas une de plus" />
          <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ETAPES.map((e, i) => (
              <Reveal key={e.num} delai={i * 0.08}>
                <li className="h-full relative pt-8">
                  <span
                    className="absolute top-0 left-0 font-display text-[46px] leading-none text-nuit/10"
                    aria-hidden="true"
                  >
                    {e.num}
                  </span>
                  <div className="h-1.5 w-14 bg-jaune mb-4" aria-hidden="true" />
                  <h3 className="font-cond uppercase font-bold tracking-[0.03em] text-[19px] text-nuit leading-tight">
                    {e.titre}
                  </h3>
                  <p className="mt-2.5 text-nuit/75 text-[16px]">{e.texte}</p>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------- CONFORMITE ---------- */}
      <section className="bg-nuit text-white py-16 sm:py-20 relative overflow-hidden">
        <div className="absolute inset-0 trame-diagonale" aria-hidden="true" />
        <div className="conteneur relative">
          <div className="grid lg:grid-cols-[1fr_0.8fr] gap-10 lg:gap-14 items-center">
            <div>
              <TitreSection
                clair
                avant="La machine arrive avec ses "
                mot="papiers"
              />
              <p className="mt-5 text-white/75 max-w-xl">
                En location, c'est votre entreprise qui reste responsable du matériel qu'elle
                fait utiliser. Notre travail, c'est de vous mettre en règle sans que vous ayez
                à courir après quoi que ce soit.
              </p>
              <ul className="mt-7 space-y-4">
                {[
                  ['Rapport de vérification à jour', "Fourni avec la machine, à conserver pendant toute la durée de la location."],
                  ['Notice et conformité', "Notice d'utilisation du constructeur et documents de conformité remis à la livraison."],
                  ['État des lieux à l\'aller et au retour', "Signé des deux côtés. Ce qui est constaté au départ ne se rediscute pas au retour."],
                  ['Le bon CACES, dit clairement', "Catégorie A pour nos mini-pelles, catégorie F pour le chariot télescopique. L'autorisation de conduite reste délivrée par l'employeur du conducteur."],
                ].map(([t, p]) => (
                  <li key={t} className="flex gap-4">
                    <span className="mt-2 shrink-0 w-3 h-3 bg-jaune" aria-hidden="true" />
                    <div>
                      <p className="font-cond uppercase font-bold tracking-[0.04em] text-[17px]">{t}</p>
                      <p className="text-white/70 text-[16px] mt-0.5">{p}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <div className="absolute -inset-x-3 -bottom-2 h-2 bande-chantier" aria-hidden="true" />
              <img
                src="/images/ecr50d-operateur.jpg"
                alt="Conducteur en gilet de sécurité aux commandes de la mini-pelle Volvo ECR50D"
                loading="lazy"
                className="photo-entiere relative rounded-[4px] shadow-haut max-h-[460px] w-auto mx-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <AppelAction
        titre="Un doute sur le CACES ou la conformité ?"
        texte="Posez la question avant de commander. On préfère passer cinq minutes au téléphone qu'immobiliser votre chantier."
      />

      <Chiffres />

      {/* ---------- ZONE ---------- */}
      <section className="bg-white py-16 sm:py-20">
        <div className="conteneur">
          <TitreSection avant="On livre dans toute l'" mot="Île-de-France" />
          <p className="mt-4 max-w-2xl text-nuit/75">
            Notre dépôt est au 2 boulevard Carnot à Alfortville, au cœur de la petite couronne.
            De là, nos machines partent sous 24 à 48 h vers l'ensemble de la région.
          </p>
          <ul className="mt-8 flex flex-wrap gap-2.5">
            {COMMUNES_LISTE.map((c) => (
              <li
                key={c}
                className="font-cond uppercase tracking-[0.06em] text-[14px] bg-trame text-nuit px-3.5 py-2 border-l-2 border-bleu"
              >
                {c}
              </li>
            ))}
          </ul>
          <Link to="/livraison-ile-de-france" className="btn-ligne mt-8">
            Voir le détail de la livraison
          </Link>
        </div>
      </section>

      {/* ---------- GALERIE ---------- */}
      <section className="bg-trame py-16 sm:py-20">
        <div className="conteneur">
          <TitreSection avant="Le parc, sans " mot="retouche" />
          <p className="mt-4 max-w-2xl text-nuit/75">
            Au-delà des visuels constructeur, voici nos machines telles qu'elles sont :
            photographiées chez nous, avant de partir en livraison.
          </p>
          <div className="mt-9 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['/images/ecr25d-face.jpg', 'Mini-pelle Volvo ECR25D vue de face sur son porte-engins'],
              ['/images/jcb525-fourches.jpg', 'Chariot télescopique JCB 525-60 équipé de ses fourches'],
              ['/images/swe25uf-remorque.jpg', 'Mini-pelle Sunward SWE25UF chargée sur remorque'],
              ['/images/ecr50d-remorque.jpg', 'Mini-pelle Volvo ECR50D prête pour le transport'],
              ['/images/ecr25d-chargee.jpg', 'Mini-pelle Volvo ECR25D sanglée avant départ en livraison'],
              ['/images/acc-pince-de-tri.jpg', 'Pince de tri hydraulique pour le tri de gravats'],
              ['/images/acc-brh-2.jpg', 'Brise-roche hydraulique avec ses sangles de manutention'],
              ['/images/jcb525-parc.jpg', 'Chariots télescopiques JCB au parc'],
            ].map(([src, alt], i) => (
              <Reveal key={src} delai={(i % 4) * 0.06}>
                <figure className="bg-white border border-nuit/10 p-3 h-full flex items-center justify-center">
                  <img src={src} alt={alt} loading="lazy" className="photo-entiere max-h-[280px] w-auto" />
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Avis />
      <Faq />
      <SectionContact
        avant="Dites-nous ce que vous avez à "
        mot="faire"
        intro="Le type de chantier, les dates, la largeur de votre accès. On vous répond en quelques heures avec la machine qui convient et son tarif. Et si c'est urgent, décrochez votre téléphone, c'est encore plus rapide."
      />
    </>
  );
}
